import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { adminStats, demoCount, clearDemo } from "@/lib/api";
import { toast } from "sonner";
import { SCHEMAS, GROUPS } from "./schema";
import ContentManager from "./ContentManager";
import EnquiriesManager from "./EnquiriesManager";
import { SignOut, Envelope, Gauge, ArrowUpRight } from "@phosphor-icons/react";

function Overview({ go }) {
  const [stats, setStats] = useState(null);
  const [demo, setDemo] = useState(null);
  const loadDemo = () => demoCount().then(setDemo).catch(() => {});
  useEffect(() => { adminStats().then(setStats).catch(() => {}); loadDemo(); }, []);
  const removeDemo = async () => {
    if (!window.confirm("Remove ALL demo/placeholder content (sample albums, photos and events)? This cannot be undone.")) return;
    const r = await clearDemo();
    toast.success(`Removed ${r.deleted} demo item(s)`);
    loadDemo();
  };
  const cards = [
    ["New Enquiries", stats?.enquiries_new, "enquiries"],
    ["Total Enquiries", stats?.enquiries, "enquiries"],
    ["Publications", stats?.publications, "publications"],
    ["Insights", stats?.insights, "insights"],
    ["Awards", stats?.awards, "awards"],
    ["Gallery Albums", stats?.gallery_albums, "gallery_albums"],
  ];
  return (
    <div data-testid="admin-overview">
      <h2 className="font-serif text-3xl text-navy">Dashboard</h2>
      <p className="text-sm text-softgrey-600">Manage every section of the website — no code required.</p>
      {demo?.count > 0 && (
        <div data-testid="demo-banner" className="mt-5 flex flex-col gap-3 rounded-lg border border-amber-300 bg-amber-50 p-5 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <span className="mt-0.5 rounded bg-amber-400 px-2 py-0.5 text-[11px] font-bold text-navy">DEMO</span>
            <p className="text-sm text-amber-900">
              This site currently shows <strong>{demo.count}</strong> temporary placeholder item(s) (sample albums, photos and events) so you can preview the design. Replace them with real content, or remove them all in one click.
            </p>
          </div>
          <button data-testid="clear-demo" onClick={removeDemo} className="shrink-0 rounded-md bg-navy px-5 py-2.5 text-sm font-semibold text-white hover:bg-navy-700">Remove all demo content</button>
        </div>
      )}
      <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {cards.map(([label, val, target]) => (
          <button key={label} onClick={() => go(target)} className="group rounded-lg border border-slate-200 bg-white p-6 text-left transition-transform hover:-translate-y-1">
            <div className="flex items-center justify-between"><span className="text-xs uppercase tracking-wide text-softgrey-600">{label}</span><ArrowUpRight size={16} className="text-gold-dark opacity-0 transition-opacity group-hover:opacity-100" /></div>
            <div className="mt-2 font-serif text-4xl text-navy">{val ?? "—"}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [view, setView] = useState("overview");

  const doLogout = async () => { await logout(); nav("/admin/login"); };

  return (
    <div className="flex min-h-screen bg-softgrey-100" data-testid="admin-dashboard">
      <aside className="hidden w-64 shrink-0 flex-col border-r border-slate-200 bg-white md:flex">
        <div className="border-b border-slate-200 p-6">
          <div className="font-serif text-xl text-navy">Admin Panel</div>
          <div className="overline text-gold-dark">Dr. Bhawna Sirohi</div>
        </div>
        <nav className="flex-1 overflow-y-auto p-4 text-sm">
          <button onClick={() => setView("overview")} data-testid="nav-overview" className={`mb-1 flex w-full items-center gap-2 rounded-md px-3 py-2 text-left ${view === "overview" ? "bg-navy text-white" : "text-navy/75 hover:bg-softgrey"}`}><Gauge size={16} /> Overview</button>
          <button onClick={() => setView("enquiries")} data-testid="nav-enquiries" className={`mb-3 flex w-full items-center gap-2 rounded-md px-3 py-2 text-left ${view === "enquiries" ? "bg-navy text-white" : "text-navy/75 hover:bg-softgrey"}`}><Envelope size={16} /> Enquiries</button>
          {GROUPS.map((g) => (
            <div key={g.title} className="mb-3">
              <div className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-softgrey-400">{g.title}</div>
              {g.items.map((c) => (
                <button key={c} onClick={() => setView(c)} data-testid={`nav-${c}`} className={`flex w-full items-center rounded-md px-3 py-2 text-left text-[13px] ${view === c ? "bg-navy text-white" : "text-navy/70 hover:bg-softgrey"}`}>{SCHEMAS[c].label}</button>
              ))}
            </div>
          ))}
        </nav>
        <div className="border-t border-slate-200 p-4">
          <div className="mb-2 truncate text-xs text-softgrey-600">{user?.email}</div>
          <button onClick={doLogout} data-testid="admin-logout" className="flex w-full items-center gap-2 rounded-md border border-slate-300 px-3 py-2 text-sm font-semibold text-navy hover:bg-navy hover:text-white"><SignOut size={16} /> Sign Out</button>
        </div>
      </aside>

      <div className="flex-1">
        <div className="flex items-center justify-between border-b border-slate-200 bg-white p-4 md:hidden">
          <select value={view} onChange={(e) => setView(e.target.value)} className="rounded-md border border-slate-300 px-3 py-2 text-sm">
            <option value="overview">Overview</option><option value="enquiries">Enquiries</option>
            {GROUPS.flatMap((g) => g.items).map((c) => <option key={c} value={c}>{SCHEMAS[c].label}</option>)}
          </select>
          <button onClick={doLogout} className="text-sm font-semibold text-navy">Sign Out</button>
        </div>
        <div className="p-6 md:p-10">
          {view === "overview" && <Overview go={setView} />}
          {view === "enquiries" && <EnquiriesManager />}
          {SCHEMAS[view] && <ContentManager collection={view} />}
        </div>
      </div>
    </div>
  );
}
